<script setup lang="ts">
import { computed, ref, useId, useTemplateRef } from "vue";
import { just } from "../../lib/maybe";
import ZwField from "./ZwField.vue";

/** Compact chip-list editor for string-list fields (`aka`, `tags`, and
 * tag-shaped custom worldbuilding fields — #72). Deliberately its own
 * component rather than inlined per field: the add/dedupe/remove behavior is
 * identical everywhere it's used. */
const model = defineModel<string[]>({ default: () => [] });

const props = defineProps<{
  label?: string;
  id?: string;
  placeholder?: string;
}>();

const draft = ref("");
const input = useTemplateRef<HTMLInputElement>("input");
// `ZwField` wraps everything (chips included) in a single `<label>`. An
// unlabeled `<label>` implicitly binds to the first labelable descendant in
// DOM order — one of the chip remove buttons, since `<button>` is labelable
// too — unless `for`/`id` explicitly point at the text input instead. Callers
// (e.g. `EntityFieldsPanel`) don't always pass an `id`, so generate one
// rather than leaving the association ambiguous.
const generatedId = useId();
const inputId = computed(() => props.id ?? generatedId);

function addFromDraft(): void {
  const value = draft.value.trim();
  draft.value = "";
  if (value === "") return;
  if (model.value.some((v) => v.toLowerCase() === value.toLowerCase())) return;
  model.value = [...model.value, value];
}

function removeAt(index: number): void {
  model.value = model.value.filter((_, i) => i !== index);
}

function onKeydown(event: KeyboardEvent): void {
  if (event.key === "Enter") {
    event.preventDefault();
    addFromDraft();
  } else if (
    event.key === "Backspace" &&
    draft.value === "" &&
    model.value.length > 0
  ) {
    removeAt(model.value.length - 1);
  }
}

defineExpose({ focus: () => input.value?.focus() });
</script>

<template>
  <ZwField
    v-if="props.label !== undefined && props.label !== ''"
    :label="props.label"
    :for-id="just(inputId)"
  >
    <div class="zw-tag-list">
      <span
        v-for="(tag, index) in model"
        :key="`${tag}-${index}`"
        class="zw-tag-chip"
      >
        {{ tag }}
        <button
          type="button"
          class="zw-tag-remove"
          :aria-label="`Remove ${tag}`"
          @click="removeAt(index)"
        >
          ×
        </button>
      </span>
      <input
        :id="inputId"
        ref="input"
        v-model="draft"
        type="text"
        class="zw-tag-input"
        :placeholder="props.placeholder"
        autocomplete="off"
        writingsuggestions="false"
        spellcheck="false"
        @keydown="onKeydown"
      />
    </div>
  </ZwField>
  <div v-else class="zw-tag-list">
    <span
      v-for="(tag, index) in model"
      :key="`${tag}-${index}`"
      class="zw-tag-chip"
    >
      {{ tag }}
      <button
        type="button"
        class="zw-tag-remove"
        :aria-label="`Remove ${tag}`"
        @click="removeAt(index)"
      >
        ×
      </button>
    </span>
    <input
      :id="inputId"
      ref="input"
      v-model="draft"
      type="text"
      class="zw-tag-input"
      :placeholder="props.placeholder"
      autocomplete="off"
      writingsuggestions="false"
      spellcheck="false"
      @keydown="onKeydown"
    />
  </div>
</template>

<style>
.zw-tag-list {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.3rem;
  padding: 0.3rem 0.4rem;
  border: 1px solid var(--line);
  border-radius: 0.35rem;
  background: var(--bg-input, transparent);
}

.zw-tag-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.1rem 0.35rem;
  border-radius: 0.25rem;
  background: color-mix(in oklch, var(--color-zw-amber) 16%, transparent);
  color: var(--text-strong);
  font-size: 0.82rem;
  line-height: 1.4;
  white-space: nowrap;
}

.zw-tag-remove {
  appearance: none;
  border: 0;
  background: transparent;
  padding: 0;
  color: var(--text-muted);
  font-size: 0.95rem;
  line-height: 1;
  cursor: pointer;
}

.zw-tag-remove:hover {
  color: var(--danger);
}

.zw-tag-input {
  flex: 1;
  min-width: 6rem;
  border: 0;
  background: transparent;
  outline: none;
  font: inherit;
  font-size: 0.88rem;
  color: var(--text);
}
</style>
